<template>
  <button
    class="whitespace-no-wrap group inline-flex items-center justify-center gap-1 transition duration-100 ease-linear focus-visible:ring-2 focus-visible:ring-gray-secondary"
    :class="{
      'p-3': !noPadding,
      'hover:text-primary-600 px-0 text-primary': isGhost,
      'bg-secondary hover:bg-secondary-600 border-b px-0': isUnderlined,
      'font-semibold border border-primary bg-primary text-white hover:bg-primary-400':
        isPrimary,
      'font-semibold bg-secondary hover:bg-secondary-600 border text-primary hover:text-primary-400':
        isSecondary,
      'font-semibold hover:bg-secondary-300 border border-primary bg-transparent text-primary hover:text-primary-400':
        isTertiary,
      'w-full': isFullWidth,
      'animate-pulse cursor-not-allowed': loading,
      'bg-lightGold': isGold,
      'font-semibold hover:bg-secondary-600 gap-2 border px-2 py-1 text-base capitalize text-gray-secondary ring-gray-secondary hover:ring-1': isMuted,
      'border-gray-secondary bg-gray-secondary text-white': isActive,
      'cursor-not-allowed !border-[#767676] !bg-[#767676] !text-white': disabled,
      uppercase: isCapitalised,
    }"
    :disabled="disabled"
    :type="nativeType"
    @click="clickHandler"
  >
    <slot name="icon">
      <Icon
        v-if="icon"
        :icon="icon"
        class="inline-block size-5"
        :class="[
          {
            'group-hover:animate-ping-small': animateIcon,
          },
          textColorClasses,
        ]"
      />
    </slot>

    <slot />
    <slot name="badge" :badge="badge">
      <Transition
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        enter-active-class="transition ease-linear duration-200"
        leave-active-class="transition ease-linear duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <span v-show="badge" :class="textColorClasses"> ({{ badge }}) </span>
      </Transition>
    </slot>
  </button>
</template>

<script setup lang="ts">
const props = defineProps({
  type: {
    type: String as PropType<string>,
    default: 'primary',
    validator: (val: string) =>
      [
        'primary',
        'secondary',
        'tertiary',
        'ghost',
        'gold',
        'muted',
        'underlined',
      ].includes(val),
  },
  nativeType: {
    type: String as PropType<'button' | 'submit' | 'reset'>,
    default: 'button',
  },
  noPadding: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  icon: {
    type: String as PropType<string>,
    default: '',
  },
  animateIcon: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  disabled: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  isFullWidth: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  loading: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  badge: {
    type: Number as PropType<number>,
    default: 0,
  },
  ignoreCapitalisation: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  stopPropagation: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  preventDefault: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  isActive: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

const emit = defineEmits(['click'])

const isPrimary = computed(() => props.type === 'primary')
const isSecondary = computed(() => props.type === 'secondary')
const isTertiary = computed(() => props.type === 'tertiary')
const isGhost = computed(() => props.type === 'ghost')
const isGold = computed(() => props.type === 'gold')
const isMuted = computed(() => props.type === 'muted')
const isUnderlined = computed(() => props.type === 'underlined')
const isCapitalised = computed(
  () =>
    !props.ignoreCapitalisation &&
    (isPrimary.value || isSecondary || isTertiary),
)

const clickHandler = (e: MouseEvent) => {
  if (props.nativeType !== 'submit') {
    if (props.preventDefault) {
      e.preventDefault()
    }
    if (props.stopPropagation) {
      e.stopPropagation()
    }
  }
  emit('click')
}

const textColorClasses = computed(() => ({
  'text-white': isPrimary.value,
  'text-primary-100': isSecondary.value || isMuted.value,
  'text-primary': isTertiary.value || isGhost.value,
}))
</script>
